/**
 * WebSocket client with reconnect and backoff.
 *
 * Shared rather than per-surface. It lived in the web app, which is why mobile
 * had no live feed at all — the class existed, on the other side of an app
 * boundary. Nothing in it is browser-specific: React Native provides
 * `WebSocket`, `setInterval` and `JSON`, so the same file drives the phone.
 *
 * Where the socket LIVES still differs per surface — the browser derives it
 * from `location`, the desktop shell is handed an absolute origin at boot, the
 * phone reads a baked-in URL — so the URL is a constructor argument and each
 * surface answers it its own way.
 *
 * Mobile connections drop constantly; a realtime client that does not reconnect
 * cleanly is a realtime client that shows stale data confidently, which is the
 * one thing a trading tool must never do.
 */
import type { Analysis, Bar, MarketStatus } from './types.js';

export interface TickMessage {
  t: 'tick'; s: string; p: number; b: number; a: number; ts: number; d: number;
}
export interface BarMessage {
  t: 'bar'; s: string; tf: string; bar: Bar; complete: boolean;
}
export interface AnnMessage { t: 'ann'; s: string; analysis: Analysis; }
export interface StatusMessage { t: 'status'; s: string; status: MarketStatus; }
export interface HelloMessage { t: 'hello'; deviceId: string; mode: string; symbols: string[]; }
export interface ErrorMessage { t: 'error'; code: string; message: string; }

export type ServerMessage =
  | HelloMessage | TickMessage | BarMessage | AnnMessage | StatusMessage
  | ErrorMessage | { t: 'pong' };

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface FeedHandlers {
  onTick?(m: TickMessage): void;
  onBar?(m: BarMessage): void;
  onAnalysis?(m: AnnMessage): void;
  onStatus?(m: StatusMessage): void;
  onHello?(m: HelloMessage): void;
  onError?(m: ErrorMessage): void;
  onState?(s: ConnectionState): void;
}

export class Feed {
  private ws: WebSocket | null = null;
  private symbols = new Set<string>();
  private attempt = 0;
  private closedByUser = false;
  private heartbeat: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly url: string,
    private readonly handlers: FeedHandlers,
  ) {}

  connect(): void {
    this.closedByUser = false;
    this.handlers.onState?.(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.attempt = 0;
      this.handlers.onState?.('open');
      if (this.symbols.size > 0) {
        ws.send(JSON.stringify({ t: 'sub', symbols: [...this.symbols] }));
      }
      // Keep the socket warm. On mobile this prevents the radio dropping to
      // idle, which otherwise adds 100-500ms to the first message after a quiet
      // period.
      this.heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'ping' }));
      }, 25_000);
    });

    ws.addEventListener('message', (ev) => {
      let m: ServerMessage;
      try {
        m = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      switch (m.t) {
        case 'tick': this.handlers.onTick?.(m); break;
        case 'bar': this.handlers.onBar?.(m); break;
        case 'ann': this.handlers.onAnalysis?.(m); break;
        case 'status': this.handlers.onStatus?.(m); break;
        case 'hello': this.handlers.onHello?.(m); break;
        case 'error': this.handlers.onError?.(m); break;
        default: break;
      }
    });

    const reconnect = (): void => {
      if (this.heartbeat) { clearInterval(this.heartbeat); this.heartbeat = null; }
      if (this.closedByUser) { this.handlers.onState?.('closed'); return; }
      this.handlers.onState?.('reconnecting');
      // Exponential backoff with jitter, capped. Never hammer a struggling server.
      const base = Math.min(15_000, 500 * 2 ** this.attempt);
      const delay = base * (0.7 + Math.random() * 0.6);
      this.attempt++;
      setTimeout(() => this.connect(), delay);
    };

    ws.addEventListener('close', reconnect);
    ws.addEventListener('error', () => ws.close());
  }

  subscribe(symbols: string[]): void {
    for (const s of symbols) this.symbols.add(s);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: 'sub', symbols }));
    }
  }

  unsubscribe(symbols: string[]): void {
    for (const s of symbols) this.symbols.delete(s);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: 'unsub', symbols }));
    }
  }

  /** Replace the subscription set in one round trip. */
  setSymbols(symbols: string[]): void {
    const next = new Set(symbols);
    const toRemove = [...this.symbols].filter((s) => !next.has(s));
    const toAdd = symbols.filter((s) => !this.symbols.has(s));
    if (toRemove.length) this.unsubscribe(toRemove);
    if (toAdd.length) this.subscribe(toAdd);
  }

  close(): void {
    this.closedByUser = true;
    if (this.heartbeat) { clearInterval(this.heartbeat); this.heartbeat = null; }
    this.ws?.close();
    this.handlers.onState?.('closed');
  }
}
